import { ChevronDownIcon, ChevronUpIcon } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React, { useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

export interface AccordionProps {
    icon: React.ComponentType<{ size?: number; color?: string }>;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    defaultExpanded?: boolean;
    onToggle?: (isExpanded: boolean) => void;
    contentPadding?: number;
}

export function Accordion({
    icon: Icon,
    title,
    subtitle,
    children,
    defaultExpanded = false,
    onToggle,
    contentPadding = 16,
}: AccordionProps) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // Mobile-only: forces title/subtitle right-aligned in Arabic regardless of
    // Typography's own RTL default. Web is left exactly as it was before this
    // pass — untouched, so its existing Arabic behavior can't be affected.
    const isMobileRTL = Platform.OS !== 'web' && isRTL;

    const handleToggle = () => {
        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);
        onToggle?.(newExpanded);
    };

    const chevron = isExpanded ? (
        <ChevronUpIcon size={24} color={colors.fontPrimary} />
    ) : (
        <ChevronDownIcon size={24} color={colors.fontPrimary} />
    );

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <TouchableOpacity
                style={[
                    styles.header,
                    // direction:'ltr' makes flexDirection immune to any ambient/native
                    // RTL auto-mirroring — 'row-reverse' below is then the ONLY thing
                    // controlling visual order, deterministically, on mobile. Flat
                    // (not nested) so icon and text — both direct children here —
                    // land on adjacent edges instead of getting spread apart by a
                    // second, independent reversal inside a wide nested row.
                    isMobileRTL && { direction: 'ltr', flexDirection: 'row-reverse' },
                    Platform.OS === 'web' && styles.headerWeb,
                ]}
                onPress={handleToggle}
                activeOpacity={0.7}
            >
                <View style={Platform.OS === 'web' ? styles.iconWrapWeb : undefined}>
                    <Icon size={24} color={colors.fontPrimary} />
                </View>
                <View style={styles.textStack}>
                    <Typography
                        variant="headline"
                        color="primary"
                        style={isMobileRTL ? styles.titleRTL : undefined}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="footnote"
                            color="tertiary"
                            style={isMobileRTL ? styles.subtitleRTL : undefined}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </View>
                {chevron}
            </TouchableOpacity>

            {isExpanded && (
                <View style={[styles.content, { paddingHorizontal: contentPadding }]}>
                    {children}
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        borderRadius: 12,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 9,
        gap: 8,
    },
    headerWeb: {
        alignItems: 'flex-start',
    },
    iconWrapWeb: {
        flexShrink: 0,
        alignSelf: 'flex-start',
    },
    titleRTL: {
        textAlign: 'right',
    },
    subtitleRTL: {
        textAlign: 'right',
    },
    textStack: {
        flex: 1,
        minWidth: 0,
        gap: 4,
    },
    content: {
        paddingBottom: 24,
    },
});

export default Accordion;
